import { useEffect, useRef, useState } from 'react'
import * as Icons from 'lucide-react'
import { useApp } from '../store.jsx'
import { initials, avatarColor, fmt } from '../data.js'
import { notifyPermission, requestNotifyPermission } from '../lib/browserNotify'

// Generic icon wrapper - converts kebab-case lucide name to PascalCase component
export function Icon({ name, size = 16, className = '', ...rest }) {
  if (!name) return null
  const compName = name.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('')
  const Cmp = Icons[compName] || Icons[name] || Icons.Square
  return <Cmp size={size} className={className} {...rest} />
}

// Avatar circle
export function Avatar({ name, size = 'md', seed, color }) {
  const cls = size === 'lg' ? 'avatar lg' : size === 'sm' ? 'avatar sm' : 'avatar'
  const bg = color || `linear-gradient(135deg,${avatarColor((seed ?? name?.length ?? 0) % 8)},#7C3AED)`
  return <div className={cls} style={{ background: bg }}>{initials(name || '?')}</div>
}

// Stat card for dashboards
export function StatCard({ label, value, delta, dir = 'up', icon, color = 'primary' }) {
  return (
    <div className="stat-card">
      <div className="top">
        <div><div className="label">{label}</div></div>
        <div className={`icon ${color}`}><Icon name={icon} size={18} /></div>
      </div>
      <div className="value">{value}</div>
      {delta && (
        <div className={`delta ${dir}`}>
          <Icon name={dir === 'up' ? 'trending-up' : 'trending-down'} size={13} />
          {delta} <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>vs last period</span>
        </div>
      )}
    </div>
  )
}

// Badge pill
export function Badge({ variant = 'muted', children, dot, style }) {
  return (
    <span className={`badge badge-${variant}`} style={style}>
      {dot && <span className="badge-dot" />}
      {children}
    </span>
  )
}

// Toast container
export function Toasts() {
  const { toasts, dismissToast } = useApp()
  return (
    <div className="toast-container">
      {toasts.map(t => {
        const ico = t.type === 'success' ? 'check-circle' : t.type === 'error' ? 'alert-circle' : 'info'
        return (
          <div key={t.id} className={`toast ${t.type}`} onClick={() => dismissToast(t.id)}>
            <Icon name={ico} size={18} />
            <span>{t.msg}</span>
            <button className="toast-close" onClick={(e) => { e.stopPropagation(); dismissToast(t.id); }}>
              <Icon name="x" size={14} />
            </button>
          </div>
        )
      })}
    </div>
  )
}

// Modal host with focus trapping
export function Modal() {
  const { modal, closeModal } = useApp()
  const ref = useRef(null)
  const prevFocus = useRef(null)
  useEffect(() => {
    if (!modal) return
    prevFocus.current = document.activeElement
    const focusableSel = 'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
    const timer = setTimeout(() => {
      if (ref.current) {
        const f = ref.current.querySelectorAll(focusableSel)
        if (f.length) f[0].focus()
      }
    }, 0)
    const onKey = (e) => {
      if (e.key === 'Escape') { closeModal(); return }
      if (e.key === 'Tab' && ref.current) {
        const f = ref.current.querySelectorAll(focusableSel)
        if (!f.length) { e.preventDefault(); return }
        const first = f[0], last = f[f.length - 1]
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      clearTimeout(timer)
      if (prevFocus.current) prevFocus.current.focus()
    }
  }, [modal, closeModal])
  if (!modal) return null
  return (
    <div className="modal-back open" onClick={(e) => { if (e.target === e.currentTarget) closeModal() }}>
      <div ref={ref} className={`modal ${modal.size || ''}`}>{modal.content}</div>
    </div>
  )
}

// Skeleton loading placeholders
export function SkeletonTable({ rows = 5, cols = 4 }) {
  return (
    <div className="table-wrap">
      <table className="tbl skeleton">
        <thead>
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i}><div className="skeleton-line" style={{ width: '60%' }} /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c}><div className="skeleton-line" style={{ width: `${50 + Math.random() * 40}%` }} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function SkeletonCard({ lines = 3 }) {
  return (
    <div className="card" style={{ padding: 24 }}>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="skeleton-line" style={{ width: `${70 - i * 15}%`, marginBottom: i < lines - 1 ? 12 : 0 }} />
      ))}
    </div>
  )
}

// Scroll-to-top
export function ScrollTop() {
  const [show, setShow] = useState(false)
  useEffect(() => {
    const onScroll = () => setShow((window.scrollY || 0) > 320)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  return (
    <button className={`scroll-top ${show ? 'show' : ''}`} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Scroll to top">
      <Icon name="arrow-up" size={20} />
    </button>
  )
}

// Theme toggle button (icon-btn style)
export function ThemeToggleBtn({ width = 36 }) {
  const { state, toggleTheme } = useApp()
  const t = state.settings.theme
  return (
    <button className="icon-btn" onClick={toggleTheme} title="Toggle theme" style={{ width, height: width }}>
      <Icon name={t === 'light' ? 'sun' : 'moon'} size={16} />
    </button>
  )
}

// Severity → icon + accent colour for a notification row.
const NOTIF_SEVERITY = {
  alert: { icon: 'alert-triangle', color: 'var(--error, #EF4444)' },
  warning: { icon: 'alert-circle', color: 'var(--warning, #F59E0B)' },
  info: { icon: 'bell', color: 'var(--primary, #8B5CF6)' },
}

// Notifications drawer — real, server-backed feed (NotificationsPoller fills it).
export function NotificationsDrawer() {
  const {
    notifications, markNotifRead, markAllNotifsRead, notifOpen, setNotifOpen,
    goPage, browserNotifyEnabled, setBrowserNotify,
  } = useApp()
  const list = notifications || []
  const unread = list.filter(n => !n.readAt).length
  const [perm, setPerm] = useState(notifyPermission())

  const enable = async () => {
    const p = await requestNotifyPermission()
    setPerm(p)
    if (p === 'granted') setBrowserNotify(true)
  }

  const onItemClick = (n) => {
    if (!n.readAt) markNotifRead(n.id)
    if (n.targetType === 'report') { goPage('reports'); setNotifOpen(false) }
  }

  return (
    <div className={`drawer-back ${notifOpen ? 'open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) setNotifOpen(false) }}>
      <div className="drawer">
        <div className="drawer-head">
          <div>
            <h3 style={{ fontSize: 17 }}>Notifications</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{unread} unread</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-ghost btn-sm" disabled={!unread} onClick={() => markAllNotifsRead()}>Mark all read</button>
            <button className="icon-btn" onClick={() => setNotifOpen(false)}><Icon name="x" size={16} /></button>
          </div>
        </div>

        {perm !== 'granted' && perm !== 'unsupported' && (
          <div style={{ margin: '10px 14px 0', padding: '10px 12px', borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon name="bell-ring" size={16} style={{ color: 'var(--primary, #8B5CF6)', flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 12.5, color: 'var(--text-muted)' }}>
              {perm === 'denied'
                ? 'Browser alerts are blocked. Re-enable them in your browser’s site settings.'
                : 'Get a desktop alert the moment a report comes in.'}
            </div>
            {perm === 'default' && (
              <button className="btn btn-primary btn-sm" onClick={enable}>Enable</button>
            )}
          </div>
        )}

        <div className="drawer-body">
          {list.length === 0 ? (
            <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <Icon name="bell-off" size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
              <div style={{ fontSize: 13 }}>No notifications yet</div>
            </div>
          ) : list.map(n => {
            const s = NOTIF_SEVERITY[n.severity] || NOTIF_SEVERITY.info
            return (
              <div key={n.id} className={`notif-item ${!n.readAt ? 'unread' : ''}`} onClick={() => onItemClick(n)} style={{ cursor: 'pointer' }}>
                <div className="notif-icon" style={{ color: s.color }}><Icon name={s.icon} size={16} /></div>
                <div className="notif-body">
                  <div className="ttl">{n.title}</div>
                  {n.body && <div className="msg">{n.body}</div>}
                  <div className="time">{fmt.rel(n.createdAt)}</div>
                </div>
              </div>
            )
          })}
        </div>

        {perm === 'granted' && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12.5 }}>
            <span style={{ color: 'var(--text-muted)' }}>Desktop alerts</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setBrowserNotify(!browserNotifyEnabled)}>
              {browserNotifyEnabled ? 'On' : 'Off'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// Empty state
export function EmptyState({ title, desc, icon, cta, onCta }) {
  return (
    <div className="card empty-state">
      <div className="ico"><Icon name={icon} size={28} /></div>
      <h3>{title}</h3>
      <p>{desc}</p>
      {cta && <button className="btn btn-primary" onClick={onCta}><Icon name="plus" size={16} /> {cta}</button>}
    </div>
  )
}

// Pagination — manual entry for rows-per-page + jump-to-page input
export function Pagination({ page, pages, total, from, to, pageSize, onPage, onPageSize }) {
  const commitRows = (v) => {
    const n = parseInt(v, 10)
    if (Number.isFinite(n) && n > 0 && n <= 500) onPageSize(n)
  }
  const commitPage = (v) => {
    const n = parseInt(v, 10)
    if (Number.isFinite(n) && n >= 1 && n <= pages) onPage(n)
  }
  const inputStyle = {
    width: 56,
    height: 28,
    padding: '0 8px',
    background: 'var(--surface-2)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    color: 'var(--text)',
    fontSize: 13,
    textAlign: 'center',
  }
  return (
    <div className="pagination">
      <div>Showing <strong>{from + 1}–{to}</strong> of <strong>{total}</strong></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div className="rows-per" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Rows per page
          <input
            type="number"
            min={1}
            max={500}
            defaultValue={pageSize}
            onBlur={(e) => commitRows(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitRows(e.target.value) }}
            style={inputStyle}
          />
        </div>
        <div className="page-btns" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            <Icon name="chevron-left" size={13} />
          </button>
          <input
            type="number"
            min={1}
            max={pages}
            value={page}
            onChange={(e) => commitPage(e.target.value)}
            style={inputStyle}
          />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>of {pages}</span>
          <button className="page-btn" disabled={page >= pages} onClick={() => onPage(page + 1)}>
            <Icon name="chevron-right" size={13} />
          </button>
        </div>
      </div>
    </div>
  )
}
